/**
 * Speed-matched locomotion playback (`q-locomotion-speed-matched-playback`, owner
 * playtest 2026-09-25: "enemies … look like they glide across the floor").
 *
 * A locomotion clip plays at `rate = ground speed ÷ authored ground speed`, where the
 * authored ground speed is the speed at which the clip's planted feet do not slip —
 * MEASURED from the served glTF by `tools/rig/locomotion-stride.mjs` and carried as
 * data on the asset-pack entry (`AssetEntry.locomotion`, Principle 2). Ground speed
 * is read from the RENDERED position (the interpolated remote position, the predicted
 * + corrected own position), smoothed over `SPEED_SMOOTHING_SECONDS` so server-tick
 * jitter never flickers the rate — acceleration then comes for free.
 *
 * Pure + node-safe (AD-120): no `three`, no DOM, no clock. Off the per-tick path
 * (AD-031): presentation only, the sim is untouched.
 */

/** Slowest playback a locomotion clip may run at (a creeping start never freezes the legs). */
export const LOCOMOTION_RATE_MIN = 0.25;
/**
 * Fastest playback. `[tune]` — measured 2026-09-25, every rigged creature's `Walk` is
 * authored with a stride far shorter than its archetype speed needs (tinscale-darter
 * 0.128 m/s vs 2.75 m/s ⇒ 21×; vein-crawler-hatchling 0.05 vs 3.6 ⇒ 72×), so the cap
 * decides what most creatures show. 12× puts a 1.2 s walk cycle at 10 steps a second —
 * a scurry, not a blur. The residual slip above the cap is an ART gap (a run clip with a
 * longer stride), which `pickLocomotionClip` adopts with no code change once measured.
 */
export const LOCOMOTION_RATE_MAX = 12;
/** Time constant of the render-speed smoother, seconds. */
export const SPEED_SMOOTHING_SECONDS = 0.15;
/**
 * A walk is preferred over the run while it would play at no more than this rate; past
 * it the run takes over (never over-drive a walk when the rig has a run). Hysteresis:
 * back to the walk only below `WALK_RETURN_RATE`.
 */
export const WALK_MAX_RATE = 1.6;
export const WALK_RETURN_RATE = 1.3;
/** A position jump faster than this is a teleport/respawn/correction snap, not travel. */
export const MAX_PLAUSIBLE_SPEED_MPS = 30;

/** Authored ground speed per clip name (m/s at rate 1) — `AssetEntry.locomotion` flattened. */
export type LocomotionSpeeds = Readonly<Record<string, number>>;

/** `AssetEntry.locomotion` → clip → speed, keeping only usable (finite, > 0) values. */
export function locomotionSpeedsFrom(
  locomotion: Readonly<Record<string, { groundSpeedMps?: unknown }>> | undefined,
): LocomotionSpeeds {
  const out: Record<string, number> = {};
  if (locomotion === undefined || locomotion === null) return out;
  for (const [clip, facts] of Object.entries(locomotion)) {
    const speed = facts?.groundSpeedMps;
    if (typeof speed === 'number' && Number.isFinite(speed) && speed > 0) out[clip] = speed;
  }
  return out;
}

/**
 * The playback rate that keeps a clip's feet planted at `groundSpeedMps`, clamped to
 * [`LOCOMOTION_RATE_MIN`, `LOCOMOTION_RATE_MAX`]. `null` when the clip has no authored
 * speed (the caller then leaves it at its authored rate — today's behaviour).
 */
export function locomotionRate(
  groundSpeedMps: number,
  authoredSpeedMps: number | undefined,
): number | null {
  if (authoredSpeedMps === undefined || !(authoredSpeedMps > 0)) return null;
  const speed = Number.isFinite(groundSpeedMps) && groundSpeedMps > 0 ? groundSpeedMps : 0;
  const raw = speed / authoredSpeedMps;
  return Math.min(LOCOMOTION_RATE_MAX, Math.max(LOCOMOTION_RATE_MIN, raw));
}

/**
 * Which travelling clip to show: the rig's resolved `move` clip (run-preferring), or its
 * walk while the walk would play at a comfortable rate. `current` is the clip showing now
 * (for hysteresis). A rig with one clip, or without speeds for both, always shows `move`.
 */
export function pickLocomotionClip(
  groundSpeedMps: number,
  moveClip: string,
  walkClip: string | null,
  speeds: LocomotionSpeeds,
  current: string | null,
): string {
  if (walkClip === null || walkClip === moveClip) return moveClip;
  const walkSpeed = speeds[walkClip];
  if (walkSpeed === undefined || speeds[moveClip] === undefined) return moveClip;
  const walkRate = groundSpeedMps / walkSpeed;
  const limit = current === walkClip ? WALK_MAX_RATE : WALK_RETURN_RATE;
  return walkRate <= limit ? walkClip : moveClip;
}

/**
 * Smoothed horizontal speed of one rendered entity, from its per-frame render position.
 * Exponential smoothing with time constant `SPEED_SMOOTHING_SECONDS`; the first sample
 * and any implausible jump (a teleport/respawn) reset the baseline without reading as
 * speed.
 */
export class GroundSpeedTracker {
  private lastX: number | null = null;
  private lastZ = 0;
  private smoothed = 0;

  /** Feed the render position after `dtMs` of frame time; returns the smoothed m/s. */
  observe(x: number, z: number, dtMs: number): number {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return this.smoothed;
    if (this.lastX === null || !(dtMs > 0)) {
      this.lastX = x;
      this.lastZ = z;
      return this.smoothed;
    }
    const dt = dtMs / 1000;
    const raw = Math.hypot(x - this.lastX, z - this.lastZ) / dt;
    this.lastX = x;
    this.lastZ = z;
    if (raw > MAX_PLAUSIBLE_SPEED_MPS) return this.smoothed; // a snap, not travel
    const k = 1 - Math.exp(-dt / SPEED_SMOOTHING_SECONDS);
    this.smoothed += (raw - this.smoothed) * k;
    return this.smoothed;
  }

  /** The last smoothed speed, m/s. */
  get speed(): number {
    return this.smoothed;
  }
}
